var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef, useCallback, useState, useLayoutEffect } from 'react';
import { fetchSwapQuote } from '../utils/zapSwapUtils';
const amountsEqual = (a, b) => {
    if (!a || !b)
        return false;
    return parseFloat(a.trim()) === parseFloat(b.trim());
};
export function usePollingSwapQuote({ payAmount, payToken, receiveToken, slippageValue, enabled, intervalMs = 10000, // Default 7 seconds
onQuoteUpdate, onLoadingChange, currentQuotePayAmount, }) {
    const intervalRef = useRef(null);
    const isMountedRef = useRef(true);
    const timeoutRef = useRef(null);
    const previousQuoteRef = useRef(null);
    const [newQuoteArrived, setNewQuoteArrived] = useState(false);
    const onQuoteUpdateRef = useRef(onQuoteUpdate);
    const onLoadingChangeRef = useRef(onLoadingChange);
    const payAmountRef = useRef(payAmount);
    const payTokenRef = useRef(payToken);
    const receiveTokenRef = useRef(receiveToken);
    const slippageValueRef = useRef(slippageValue);
    const enabledRef = useRef(enabled);
    const currentQuotePayAmountRef = useRef(currentQuotePayAmount);
    useLayoutEffect(() => {
        onQuoteUpdateRef.current = onQuoteUpdate;
        if (onLoadingChange) {
            onLoadingChangeRef.current = onLoadingChange;
        }
        payAmountRef.current = payAmount;
        payTokenRef.current = payToken;
        receiveTokenRef.current = receiveToken;
        slippageValueRef.current = slippageValue;
        enabledRef.current = enabled;
        currentQuotePayAmountRef.current = currentQuotePayAmount;
    }, [
        onQuoteUpdate,
        onLoadingChange,
        payAmount,
        payToken,
        receiveToken,
        slippageValue,
        enabled,
        currentQuotePayAmount,
    ]);
    const fetchQuote = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const currentPayAmount = payAmountRef.current;
        const currentPayToken = payTokenRef.current;
        const currentReceiveToken = receiveTokenRef.current;
        const currentSlippageValue = slippageValueRef.current;
        const currentEnabled = enabledRef.current;
        if (!currentEnabled ||
            !currentPayAmount ||
            parseFloat(currentPayAmount.trim()) <= 0 ||
            !currentPayToken ||
            !currentReceiveToken) {
            return;
        }
        try {
            // Notify that loading has started
            if (onLoadingChangeRef.current) {
                onLoadingChangeRef.current(true);
            }
            const quote = yield fetchSwapQuote({
                payAmount: currentPayAmount,
                payToken: currentPayToken,
                receiveToken: currentReceiveToken,
                slippageValue: currentSlippageValue,
            });
            if (isMountedRef.current) {
                const quoteChanged = !previousQuoteRef.current ||
                    previousQuoteRef.current.receiveAmount !== (quote === null || quote === void 0 ? void 0 : quote.receiveAmount) ||
                    previousQuoteRef.current.slippage !== (quote === null || quote === void 0 ? void 0 : quote.slippage);
                if (quoteChanged && quote) {
                    previousQuoteRef.current = quote;
                    onQuoteUpdateRef.current(quote);
                    setNewQuoteArrived(true);
                }
                else if (quote) {
                    previousQuoteRef.current = quote;
                    onQuoteUpdateRef.current(quote);
                }
                else {
                    previousQuoteRef.current = null;
                    onQuoteUpdateRef.current(null);
                }
                // Notify that loading has finished
                if (onLoadingChangeRef.current) {
                    onLoadingChangeRef.current(false);
                }
            }
        }
        catch (error) {
            console.error('[usePollingSwapQuote] Error fetching swap quote in polling:', error);
            if (isMountedRef.current) {
                onQuoteUpdateRef.current(null);
                // Notify that loading has finished even on error
                if (onLoadingChangeRef.current) {
                    onLoadingChangeRef.current(false);
                }
            }
        }
    }), []);
    useEffect(() => {
        if (newQuoteArrived) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    setNewQuoteArrived(false);
                }
                timeoutRef.current = null;
            }, 700);
            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            };
        }
    }, [newQuoteArrived]);
    useEffect(() => {
        isMountedRef.current = true;
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (enabled) {
            const shouldFetchImmediately = !currentQuotePayAmount || !amountsEqual(currentQuotePayAmount, payAmount);
            if (shouldFetchImmediately) {
                fetchQuote();
            }
            intervalRef.current = setInterval(() => {
                fetchQuote();
            }, intervalMs);
        }
        return () => {
            isMountedRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [
        enabled,
        intervalMs,
        payAmount,
        currentQuotePayAmount,
        payToken === null || payToken === void 0 ? void 0 : payToken.tokenId,
        receiveToken === null || receiveToken === void 0 ? void 0 : receiveToken.tokenId,
        fetchQuote,
    ]);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);
    return { fetchQuote, newQuoteArrived };
}
